// ================================================================
// ParentDashboard.jsx — نسخه نهایی با داده‌های ۱۰۰٪ واقعی
// ================================================================

import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ParentDashboard.css';
import { useAuth } from '../../context/AuthContext';
import { useExams } from '../Exams/ExamContext.jsx';

import { useCoins } from '../../context/CoinContext.jsx';
import { engine } from '../../core/books/storage.js';
import { getToken } from '../../utils/secureStorage';

// ================================================================
// Helpers
// ================================================================
const FA = '۰۱۲۳۴۵۶۷۸۹';
const fa = (n) => String(n ?? '').replace(/\d/g, (d) => FA[d]);
const toNum = (v) => { const n = Number(v); return isFinite(n) ? n : 0; };

const SUBJECT_COLORS = [
  '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#6366F1', '#84CC16', '#F97316',
];

const colorForSubject = (name, idx = 0) => {
  if (!name) return SUBJECT_COLORS[idx % SUBJECT_COLORS.length];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return SUBJECT_COLORS[hash % SUBJECT_COLORS.length];
};

const formatDuration = (min) => {
  const m = Math.round(toNum(min));
  if (!m) return '۰ دقیقه';
  if (m < 60) return `${fa(m)} دقیقه`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (mm === 0) return `${fa(h)} ساعت`;
  return `${fa(h)} ساعت و ${fa(mm)} دقیقه`;
};


const formatTime = (d) => {
  try { return new Intl.DateTimeFormat('fa-IR', { hour: '2-digit', minute: '2-digit' }).format(d); }
  catch { return ''; }
};

const getUserDisplayName = (u) => {
  if (!u) return 'دانش‌آموز';
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
  if (u.firstName) return u.firstName;
  if (u.name) return u.name;
  if (u.username) return u.username;
  return 'دانش‌آموز';
};

const NICE_STEPS = [
  5, 10, 15, 20, 30, 40, 50, 75, 100,
  150, 200, 300, 500, 750, 1000,
  2000, 3000, 5000, 10000,
];
const niceMax = (v) => {
  if (v <= 0) return 5;
  const found = NICE_STEPS.find((s) => s >= v);
  return found || Math.ceil(v / 5000) * 5000;
};

const ymd = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

// ================================================================
// بازه‌های زمانی
// ================================================================
// ================================================================
// Date helpers
// ================================================================
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const PERIOD_LABEL = {
  today: 'امروز',
  week: 'این هفته',
  month: 'این ماه',
  year: 'این سال',
};

// ================================================================
// بازه‌های زمانی — Single Source of Truth
// (هم‌دامنه‌سازی buckets و bounds در یک تابع)
// ================================================================
const resolveRange = (period) => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const tomorrowStart = addDays(todayStart, 1);
  const buckets = [];

  if (period === 'today') {
    // ⚠️ dailyProgress گرانولاریتی روزانه دارد → ۱ باکت برای کل روز
    // (نمایش ۴-۶ باکت ساعتی باعث ۶× overcount می‌شد)
    buckets.push({
      start: todayStart,
      end: tomorrowStart,
      label: 'امروز',
    });
  } else if (period === 'week') {
    const todayKey = ymd(now);
    for (let i = 6; i >= 0; i--) {
      const s = addDays(todayStart, -i);
      const e = addDays(s, 1);
      const isToday = ymd(s) === todayKey;
      buckets.push({
        start: s,
        end: e,
        label: isToday
          ? 'امروز'
          : new Intl.DateTimeFormat('fa-IR', { weekday: 'short' }).format(s),
      });
    }
  } else if (period === 'month') {
    // ۴ هفته تقویمی (شنبه تا جمعه)، هفته جاری آخرین باکت
    const dayOfWeek = todayStart.getDay();          // 0=یک‌شنبه ... 6=شنبه
    const daysSinceSat = (dayOfWeek + 1) % 7;       // شنبه=0، جمعه=6
    const currentWeekStart = addDays(todayStart, -daysSinceSat);

    const fmt = (d) => new Intl.DateTimeFormat('fa-IR', {
      day: 'numeric',
      month: 'short',
    }).format(d);

    for (let i = 3; i >= 0; i--) {
      const weekStart = addDays(currentWeekStart, -i * 7);
      const weekEnd = addDays(weekStart, 7);
      const label = i === 0
        ? 'این هفته'
        : `${fmt(weekStart)} – ${fmt(addDays(weekEnd, -1))}`;
      buckets.push({ start: weekStart, end: weekEnd, label });
    }
  } else { // year
    for (let i = 11; i >= 0; i--) {
      const s = addMonths(startOfMonth(now), -i);
      const e = addMonths(s, 1);
      let label = '';
      try {
        label = new Intl.DateTimeFormat('fa-IR', { month: 'short' }).format(s);
      } catch { label = `${fa(s.getMonth() + 1)}`; }
      buckets.push({ start: s, end: e, label });
    }
  }

  return {
    start: buckets[0].start,
    end: buckets[buckets.length - 1].end,
    buckets,
    label: PERIOD_LABEL[period],
  };
};

// ================================================================
// Y-Axis
// ================================================================
const YAxis = ({ values, padTop = 12, padBottom = 8, H = 200, formatter }) => {
  const chartH = H - padTop - padBottom;
  return (
    <div className="y-axis">
      {values.map((v, i) => {
        const f = values.length > 1 ? i / (values.length - 1) : 0;
        const topPct = ((padTop + f * chartH) / H) * 100;
        return (
          <span key={i} className="y-tick" style={{ top: `${topPct}%` }}>
            {formatter ? formatter(v) : fa(Math.round(v))}
          </span>
        );
      })}
    </div>
  );
};

// ================================================================
// Line Chart
// ================================================================
const LineChartSVG = ({
  series, color = '#6366F1', yMax, yMin = 0,
  valueFormatter = (v) => fa(v), yAxisFormatter,
  emptyText = 'داده‌ای موجود نیست',
}) => {
  const plotRef = useRef(null);
  const [tip, setTip] = useState(null);
  const gradId = useMemo(() => 'pdg-' + Math.random().toString(36).slice(2, 9), []);

  const W = 500, H = 200;
  const padTop = 12, padBottom = 8;
  const chartH = H - padTop - padBottom;

  if (!series || series.length === 0) {
    return <div className="chart-empty">{emptyText}</div>;
  }

  const values = series.map((s) => toNum(s.value));
  const yTop = yMax || niceMax(Math.max(...values, 1));
  const yBot = yMin;
  const n = series.length;
  const stepX = n > 1 ? (W - 12) / (n - 1) : 0;

  const pts = series.map((s, i) => {
    const v = toNum(s.value);
    const x = W - 6 - i * stepX;
    const ratio = yTop === yBot ? 0 : (v - yBot) / (yTop - yBot);
    const y = padTop + chartH - Math.max(0, Math.min(1, ratio)) * chartH;
    return { x, y, v, label: s.label };
  });

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const t = 0.2;
    d += ` C ${p1.x + (p2.x - p0.x) * t} ${p1.y + (p2.y - p0.y) * t}, ` +
         `${p2.x - (p3.x - p1.x) * t} ${p2.y - (p3.y - p1.y) * t}, ` +
         `${p2.x} ${p2.y}`;
  }
  const area = `${d} L ${pts[n - 1].x} ${padTop + chartH} L ${pts[0].x} ${padTop + chartH} Z`;

  const handleMove = (e) => {
    if (!plotRef.current) return;
    const rect = plotRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    let nearest = 0, dist = Infinity;
    pts.forEach((p, i) => {
      const dd = Math.abs(p.x - svgX);
      if (dd < dist) { dist = dd; nearest = i; }
    });
    const p = pts[nearest];
    setTip({
      x: (p.x / W) * rect.width,
      y: (p.y / H) * rect.height,
      label: p.label,
      value: valueFormatter(p.v),
    });
  };

  const yTicks = [yTop, yTop * 0.75, yTop * 0.5, yTop * 0.25, yBot];

  return (
    <>
      <div className="chart-body-inner">
        <YAxis
          values={yTicks}
          padTop={padTop}
          padBottom={padBottom}
          H={H}
          formatter={yAxisFormatter}
        />
        <div
          className="chart-plot"
          ref={plotRef}
          onMouseMove={handleMove}
          onMouseLeave={() => setTip(null)}
          onTouchStart={(e) => { if (e.touches[0]) handleMove(e.touches[0]); }}
          onTouchMove={(e) => { if (e.touches[0]) handleMove(e.touches[0]); }}
          onTouchEnd={() => setTip(null)}
        >
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="chart-svg">
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <g className="svg-grid">
              {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
                <line
                  key={i}
                  x1="0"
                  y1={padTop + f * chartH}
                  x2={W}
                  y2={padTop + f * chartH}
                  stroke="currentColor"
                  strokeOpacity="0.10"
                  strokeDasharray="3 5"
                />
              ))}
            </g>
            <path d={area} fill={`url(#${gradId})`} />
            <path
              d={d}
              fill="none"
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            {pts.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={tip && tip.label === p.label ? 5 : 3.5}
                fill="var(--surface)"
                stroke={color}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
          {tip && (
            <div className="chart-tooltip" style={{ left: tip.x, top: tip.y }}>
              <div className="tt-label">{tip.label}</div>
              <div className="tt-value">{tip.value}</div>
            </div>
          )}
        </div>
      </div>
      <div className="chart-xaxis">
        {pts.map((p, i) => (
          <span
            key={i}
            className="xaxis-label"
            style={{ left: `${(p.x / W) * 100}%` }}
            title={String(series[i]?.label ?? '')}
          >
            {series[i]?.label}
          </span>
        ))}
      </div>
    </>
  );
};

// ================================================================
// Bar Chart
// ================================================================
const BarChartSVG = ({
  series, color = '#3B82F6', colorFn, yMax,
  valueFormatter = (v) => fa(v), yAxisFormatter,
  emptyText = 'داده‌ای موجود نیست',
}) => {
  const plotRef = useRef(null);
  const [tip, setTip] = useState(null);

  const W = 500, H = 200;
  const padTop = 12, padBottom = 8;
  const chartH = H - padTop - padBottom;

  if (!series || series.length === 0) {
    return <div className="chart-empty">{emptyText}</div>;
  }

  const values = series.map((s) => toNum(s.value));
  const yTop = yMax || niceMax(Math.max(...values, 1));
  const n = series.length;
  const slotW = W / n;
  const barW = slotW * 0.55;
  const gap = (slotW - barW) / 2;

  const yTicks = [yTop, yTop * 0.75, yTop * 0.5, yTop * 0.25, 0];

  const barCenters = series.map((_, i) => W - (i + 0.5) * slotW);

  return (
    <>
      <div className="chart-body-inner">
        <YAxis
          values={yTicks}
          padTop={padTop}
          padBottom={padBottom}
          H={H}
          formatter={yAxisFormatter}
        />
        <div className="chart-plot" ref={plotRef}>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="chart-svg">
            <g className="svg-grid">
              {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
                <line
                  key={i}
                  x1="0"
                  y1={padTop + f * chartH}
                  x2={W}
                  y2={padTop + f * chartH}
                  stroke="currentColor"
                  strokeOpacity="0.10"
                  strokeDasharray="3 5"
                />
              ))}
            </g>
            {series.map((s, i) => {
              const v = toNum(s.value);
              const h = Math.max(2, yTop > 0 ? (v / yTop) * chartH * 0.95 : 0);
              const x = W - (i + 1) * slotW + gap;
              const y = padTop + chartH - h;
              const c = colorFn ? colorFn(v, i) : color;
              const hovered = tip && tip.i === i;
              return (
                <rect
                  key={i}
                  x={x}
                  y={y}
                  width={barW}
                  height={h}
                  fill={c}
                  rx="4"
                  opacity={hovered ? 0.85 : 1}
                  style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                  onMouseEnter={() => {
                    const rect = plotRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setTip({
                      i,
                      x: ((x + barW / 2) / W) * rect.width,
                      y: (y / H) * rect.height,
                      label: s.label,
                      value: valueFormatter(v),
                    });
                  }}
                  onMouseLeave={() => setTip(null)}
                />
              );
            })}
          </svg>
          {tip && (
            <div className="chart-tooltip" style={{ left: tip.x, top: tip.y }}>
              <div className="tt-label">{tip.label}</div>
              <div className="tt-value">{tip.value}</div>
            </div>
          )}
        </div>
      </div>
      <div className="chart-xaxis">
        {series.map((s, i) => (
          <span
            key={i}
            className="xaxis-label"
            style={{ left: `${(barCenters[i] / W) * 100}%` }}
            title={String(s.label)}
          >
            {s.label}
          </span>
        ))}
      </div>
    </>
  );
};

// ================================================================
// Donut Chart
// ================================================================
const DonutChartSVG = ({
  series, centerLabel = 'کل', valueFormatter = (v) => fa(v),
  emptyText = 'داده‌ای موجود نیست',
}) => {
  const [tip, setTip] = useState(null);
  const data = (series || []).filter((s) => toNum(s.value) > 0);
  const total = data.reduce((a, b) => a + toNum(b.value), 0);

  if (!data.length || total === 0) {
    return <div className="chart-empty">{emptyText}</div>;
  }

  const r = 38, circ = 2 * Math.PI * r, gap = 3;
  let offset = 0;
  const segs = data.map((s, i) => {
    const v = toNum(s.value);
    const len = (v / total) * circ;
    const dash = Math.max(0, len - gap);
    const seg = (
      <circle
        key={i}
        cx="50"
        cy="50"
        r={r}
        fill="none"
        stroke={s.color}
        strokeWidth={tip?.i === i ? 17 : 15}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={-offset}
        transform="rotate(-90 50 50)"
        style={{ cursor: 'pointer', transition: 'stroke-width 0.15s' }}
        onMouseEnter={() => setTip({ i, label: s.label, value: valueFormatter(v) })}
        onMouseLeave={() => setTip(null)}
      />
    );
    offset += len;
    return seg;
  });

  return (
    <div className="donut-body">
      <div className="donut-svg-wrap" style={{ position: 'relative' }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
          {segs}
        </svg>
        <div className="donut-center">
          <div className="donut-value num">{fa(total)}</div>
          <div className="donut-label">{centerLabel}</div>
        </div>
        {tip && (
          <div className="donut-tooltip">
            <span className="tt-label">{tip.label}</span>
            <span className="tt-value">{tip.value}</span>
          </div>
        )}
      </div>
      <div className="donut-legend">
        {data.map((s, i) => (
          <div key={i} className="legend-row">
            <span className="legend-dot" style={{ background: s.color }} />
            <span className="legend-label">{s.label}</span>
            <span className="legend-count num">{fa(toNum(s.value))}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================================
// Icons
// ================================================================
const ICONS = {
  overall: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  study: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  points: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  tests: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>,
  videos: <><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></>,
  classes: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  challenges: <><circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" /></>,
  examsTaken: <><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></>,
};

const ChartIcon = ({ name, color, bg }) => (
  <div className="chart-icon" style={{ background: bg, color }}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      {ICONS[name]}
    </svg>
  </div>
);

const EmptyState = ({ icon, title, desc }) => (
  <div className="empty-state-cta">
    <div className="empty-icon">{icon}</div>
    <div className="empty-title">{title}</div>
    <div className="empty-desc">{desc}</div>
  </div>
);

const SkeletonCard = ({ full }) => (
  <div className={`chart-card skeleton ${full ? 'full' : ''}`}>
    <div className="skeleton-head">
      <div className="skeleton-block" style={{ width: 46, height: 46, borderRadius: 14 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div className="skeleton-block" style={{ width: '50%', height: 14 }} />
        <div className="skeleton-block" style={{ width: '30%', height: 10 }} />
      </div>
      <div className="skeleton-block" style={{ width: 60, height: 28 }} />
    </div>
    <div className="skeleton-block" style={{ width: '100%', height: 160 }} />
  </div>
);

// ================================================================
// محاسبه‌ی پیشرفت واقعی از منابع واقعی
// ================================================================
function computeOverallProgress(userId, user, exams) {
  // ─── ۱. پیشرفت درس‌ها (۴۰٪ وزن)
  const records = engine.get(`lesson-records-${userId}`, {}) || {};
  const progressIndex = {};
  try {
    engine.keys(`lesson-progress-${userId}-`).forEach((k) => {
      const p = engine.get(k, {}) || {};
      progressIndex[k] = new Set(p.completedStepIds || []);
    });
  } catch {}

  const lessonRatios = [];
  for (const rec of Object.values(records)) {
    if (!rec || !rec.grade || !rec.subject || !rec.folderName || !rec.lessonId) continue;
    const total = toNum(rec.totalSteps);
    if (total <= 0) continue;
    const key = `lesson-progress-${userId}-${rec.grade}-${rec.subject}-${rec.folderName}-${rec.lessonId}`;
    const completedSet = progressIndex[key] || new Set();

    // ⚠️ union — جلوگیری از double-count شدن step‌هایی که هم completed و هم quiz-passed هستند
    const passedQuizIds = new Set();
    if (rec.projectId && Array.isArray(rec.quizStepIds) && rec.quizStepIds.length > 0) {
      try {
        const qp = engine.get(`step-quiz-progress-${userId}-${rec.projectId}`, {}) || {};
        rec.quizStepIds.forEach((id) => {
          if (qp[id]?.passed === true) passedQuizIds.add(id);
        });
      } catch {}
    }
    const allDone = new Set([...completedSet, ...passedQuizIds]);

    const done = Math.min(total, allDone.size);
    lessonRatios.push(done / total);
  }

  const bookProgressPct = lessonRatios.length
    ? (lessonRatios.reduce((a, b) => a + b, 0) / lessonRatios.length) * 100
    : 0;

  // ─── ۲. میانگین واقعی آزمون‌ها (۴۰٪ وزن)
  const examPercents = [];
  (exams || []).forEach((e) => {
    if (!e || e.isChallenge || e.isStepQuiz) return;
    const r = e.results?.find((r) => String(r.studentId) === String(userId));
    if (!r || r.isFinalSubmit !== true || r.isAbsent === true) return;
    const max = (e.questions || []).reduce((s, q) => s + (toNum(q.score) || 1), 0);
    if (max <= 0) return;
    examPercents.push(Math.min(100, (toNum(r.score) / max) * 100));
  });
  const examAvgPct = examPercents.length
    ? examPercents.reduce((a, b) => a + b, 0) / examPercents.length
    : 0;

  // ─── ۳. زمان مطالعه vs هدف (۲۰٪ وزن)
  const daily = user?.dailyProgress || {};
  let last7Min = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(); d.setDate(d.getDate() - i);
    last7Min += toNum(daily[ymd(d)]);
  }
  const weeklyGoal = 300;
  const studyScorePct = Math.min(100, (last7Min / weeklyGoal) * 100);

  // ─── وزن‌دهی نهایی
  const total =
    bookProgressPct * 0.4 +
    examAvgPct * 0.4 +
    studyScorePct * 0.2;

  return Math.round(total);
}






// ================================================================
// نسخه‌ی تاریخی computeOverallProgress — برای نمودار روند
// ================================================================
function computeOverallProgressAt(userId, user, exams, referenceDate) {
  const records = engine.get(`lesson-records-${userId}`, {}) || {};
  const progressIndex = {};
  try {
    engine.keys(`lesson-progress-${userId}-`).forEach((k) => {
      const p = engine.get(k, {}) || {};
      progressIndex[k] = {
        completedSet: new Set(p.completedStepIds || []),
        lastVisitedAt: toNum(p.lastVisitedAt),
      };
    });
  } catch {}

  const refMs = referenceDate.getTime();

  const lessonRatios = [];
  for (const rec of Object.values(records)) {
    if (!rec || !rec.grade || !rec.subject || !rec.folderName || !rec.lessonId) continue;
    const total = toNum(rec.totalSteps);
    if (total <= 0) continue;
    const key = `lesson-progress-${userId}-${rec.grade}-${rec.subject}-${rec.folderName}-${rec.lessonId}`;
    const prog = progressIndex[key];
    if (!prog) continue;
    if (prog.lastVisitedAt > refMs) continue;   // ← فقط بازدیدهای قبل از مرجع

    const passedQuizIds = new Set();
    if (rec.projectId && Array.isArray(rec.quizStepIds) && rec.quizStepIds.length > 0) {
      try {
        const qp = engine.get(`step-quiz-progress-${userId}-${rec.projectId}`, {}) || {};
        rec.quizStepIds.forEach((id) => {
          if (qp[id]?.passed === true) passedQuizIds.add(id);
        });
      } catch {}
    }
    const allDone = new Set([...prog.completedSet, ...passedQuizIds]);
    lessonRatios.push(Math.min(total, allDone.size) / total);
  }
  const bookProgressPct = lessonRatios.length
    ? (lessonRatios.reduce((a, b) => a + b, 0) / lessonRatios.length) * 100
    : 0;

  const examPercents = [];
  (exams || []).forEach((e) => {
    if (!e || e.isChallenge || e.isStepQuiz) return;
    const r = e.results?.find((r) => String(r.studentId) === String(userId));
    if (!r || r.isFinalSubmit !== true || r.isAbsent === true) return;
    if (!r.submittedAt) return;
    const d = new Date(r.submittedAt);
    if (isNaN(d) || d.getTime() > refMs) return;  // ← فقط آزمون‌های قبل از مرجع
    const max = (e.questions || []).reduce((s, q) => s + (toNum(q.score) || 1), 0);
    if (max <= 0) return;
    examPercents.push(Math.min(100, (toNum(r.score) / max) * 100));
  });
  const examAvgPct = examPercents.length
    ? examPercents.reduce((a, b) => a + b, 0) / examPercents.length
    : 0;

  const daily = user?.dailyProgress || {};
  let last7Min = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(referenceDate);
    d.setDate(d.getDate() - i);
    last7Min += toNum(daily[ymd(d)]);
  }
  const studyScorePct = Math.min(100, (last7Min / 300) * 100);

  return Math.round(bookProgressPct * 0.4 + examAvgPct * 0.4 + studyScorePct * 0.2);
}





// ================================================================
// MAIN
// ================================================================
const ParentDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Theme
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('navbar2-drawer-dark') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('navbar2-drawer-dark', isDark ? '1' : '0'); } catch {}
  }, [isDark]);
  useEffect(() => {
    const h = (e) => { if (e.key === 'navbar2-drawer-dark') setIsDark(e.newValue === '1'); };
    window.addEventListener('storage', h);
    return () => window.removeEventListener('storage', h);
  }, []);

  // Contexts
  const { user } = useAuth();
  const { exams } = useExams();
  const { xp } = useCoins();

  const childIdFromUrl = searchParams.get('childId');
  const isViewingChild = !!childIdFromUrl && String(childIdFromUrl) !== String(user?.id);

  const childInfo = useMemo(() => {
    if (!childIdFromUrl) return null;
    try {
      const users = Array.isArray(window.__allUsersCache) ? window.__allUsersCache : [];
      return users.find((u) => String(u.id) === String(childIdFromUrl)) || null;
    } catch { return null; }
  }, [childIdFromUrl]);

  const userId = childIdFromUrl || user?.id || 'guest';
  const studentName = isViewingChild
    ? (childInfo ? getUserDisplayName(childInfo) : 'دانش‌آموز')
    : getUserDisplayName(user);



  // State
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [dataTick, setDataTick] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [allCourses, setAllCourses] = useState([]);



  // منابع داده (پایه)
  const sourceUser = isViewingChild
    ? (childInfo || {})
    : (user || {});

  const totalXp = isViewingChild
    ? toNum(childInfo?.xp)
    : toNum(xp);

  const streak = toNum(sourceUser?.streak) || 0;

  // ─── Fetch courses از appData (برای نمودار کلاس‌ها)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const res = await fetch('/api/db/appData', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.courses)) {
          setAllCourses(data.courses);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [dataTick]);

  // ─── رفرش خودکار
  useEffect(() => {
    const bump = () => setDataTick((t) => t + 1);
    const unsubs = [];
    try { unsubs.push(engine.on('progress:changed', bump)); } catch {}
    try { unsubs.push(engine.on('quiz:step-progress-changed', bump)); } catch {}
    try { unsubs.push(engine.on('quiz:step-passed', bump)); } catch {}

    const onStorage = (e) => {
      if (!e.key) return;
      if (
        e.key.startsWith('dailyProgress_') ||
        e.key.startsWith('weeklyProgress_') ||
        e.key.startsWith('monthlyProgress_') ||
        e.key.startsWith('bookStats_') ||
        e.key.startsWith('lesson-progress-') ||
        e.key.startsWith('lesson-records-') ||
        e.key.startsWith('step-quiz-progress-') ||
        e.key.startsWith('freestudy-progress-') ||
        e.key === 'exams'
      ) bump();
    };
    window.addEventListener('storage', onStorage);

    const onFocus = () => bump();
    const onVis = () => { if (!document.hidden) bump(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      unsubs.forEach((u) => { try { u(); } catch {} });
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);










    // ─── رفرش خودکار در نیمه‌شب تا برچسب «امروز» هرگز کهنه نشود
  useEffect(() => {
    let timer;
    const schedule = () => {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 1, 0); // ۱ ثانیه بعد از نیمه‌شب
      const ms = nextMidnight.getTime() - now.getTime();
      timer = setTimeout(() => {
        setDataTick((v) => v + 1); // → computed دوباره حساب می‌شود
        schedule();                // → برای نیمه‌شب بعدی
      }, ms);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);












  // ─── تغییر لودینگ با dataTick
  useEffect(() => {
    if (userId === 'guest' || userId === 'unknown') {
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      setLoading(false);
      setLastUpdate(new Date());
    }, 150);
    return () => clearTimeout(t);
  }, [userId, dataTick, selectedPeriod]);

  // ─── محاسبه‌ی همه‌ی سری‌ها (همه از داده‌های واقعی)
  const computed = useMemo(() => {
    if (!sourceUser || !userId || userId === 'guest') {
      return {
        overallProgress: 0,
        studySeries: [],
        overallSeries: [],
        xpSeries: [],
        xpFromExams: 0,
        examsTakenSeries: [],
        examsTakenCount: 0,
        testsSeries: [],
        testsYMax: 100,
        examStats: { total: 0, passed: 0, failed: 0, pending: 0, absent: 0, avgPercent: 0 },
        videoSeries: [],
        classSeries: [],
        challengesSeries: [],
        challengeCount: 0,
        totalStudyMinutes: 0,
        range: resolveRange(selectedPeriod),
      };
    }

    const range = resolveRange(selectedPeriod);
    const buckets = range.buckets;

    // ═══════════════════════════════════════════════════════
    // ۱) سری زمانی زمان مطالعه (از user.dailyProgress واقعی)
    // ═══════════════════════════════════════════════════════
    const daily = sourceUser.dailyProgress || {};
    const studySeries = buckets.map((b) => {
      let sum = 0;
      const cur = new Date(b.start);
      while (cur < b.end) {
        sum += toNum(daily[ymd(cur)]);
        cur.setDate(cur.getDate() + 1);
      }
      return { label: b.label, value: sum };
    });

    const totalStudyMinutes = studySeries.reduce((s, d) => s + toNum(d.value), 0);

    // ═══════════════════════════════════════════════════════
    // ۲) سری تجمعی مطالعه (برای نمودار پیشرفت کلی)
    // ═══════════════════════════════════════════════════════
    // ۲) سری روند تجمعی مطالعه (قابل اعتماد و متغیر روزبه‌روز)
    // ۲) سری روزانه مطالعه (هر نقطه فقط همان روز)
    const overallSeries = studySeries.map((d) => ({
      label: d.label,
      value: toNum(d.value),
    }));

    // ═══════════════════════════════════════════════════════
    // ۳) سری XP (تنها XP واقعیِ ثبت‌شده در نتایج آزمون‌ها)
    // ═══════════════════════════════════════════════════════
    const xpEvents = [];
    (exams || []).forEach((exam) => {
      if (!exam) return;
      const r = exam.results?.find((r) => String(r.studentId) === String(userId));
      if (!r || r.isFinalSubmit !== true || !r.submittedAt) return;
      const max = (exam.questions || []).reduce((s, q) => s + (toNum(q.score) || 1), 0);
      if (max <= 0) return;
      const pct = (toNum(r.score) / max) * 100;

    // فقط مقادیر واقعی که مدیر برای آزمون تنظیم کرده — بدون هیچ fallback
    let xpEarned = 0;
    if (exam.isChallenge) {
      xpEarned = toNum(exam.rewardXP);
    } else if (exam.isStepQuiz) {
      xpEarned = toNum(exam.rewardXP);
    } else {
      if (pct >= 90) xpEarned = toNum(exam.xpExcellent);
      else if (pct >= 75) xpEarned = toNum(exam.xpVeryGood);
      else if (pct >= 50) xpEarned = toNum(exam.xpGood);
      else xpEarned = toNum(exam.xpNeedsWork);
    }

      const date = new Date(r.submittedAt);
      if (!isNaN(date)) xpEvents.push({ date, xp: xpEarned });
    });

    const xpSeries = buckets.map((b) => {
      const inBucket = xpEvents.filter((e) => e.date >= b.start && e.date < b.end);
      return {
        label: b.label,
        value: inBucket.reduce((s, e) => s + e.xp, 0),
      };
    });
    // XP فقط در بازه‌ی انتخابی، نه کل تاریخچه
    const xpFromExams = xpEvents
      .filter((e) => e.date >= range.start && e.date < range.end)
      .reduce((s, e) => s + e.xp, 0);

    // ═══════════════════════════════════════════════════════
    // ۴) آمار آزمون‌ها (از exam.results واقعی)
    // ═══════════════════════════════════════════════════════
    const examList = [];
    let passed = 0, failed = 0, pending = 0, absent = 0;
    let totalScoreSum = 0, totalMaxSum = 0;

    (exams || []).forEach((exam) => {
      if (!exam) return;
      if (exam.isChallenge || exam.isStepQuiz) return;
      if (exam.statusType === 'archived') return;
      const r = exam.results?.find((r) => String(r.studentId) === String(userId));
      if (!r || r.isFinalSubmit !== true) return;
      if (!r.submittedAt) return;                 // ← بدون تاریخ، کلاً skip
      const date = new Date(r.submittedAt);
      if (isNaN(date)) return;                    // ← تاریخ نامعتبر skip
      if (date < range.start || date >= range.end) return;  // ← هم‌دامنه با buckets
  

      const max = (exam.questions || []).reduce((s, q) => s + (toNum(q.score) || 1), 0);
      const score = toNum(r.score);
      const percent = max > 0 ? (score / max) * 100 : 0;
      const isPassed = percent >= 50;

      if (r.isAbsent) absent++;
      else if (r.isEssayGraded === false) pending++;
      else if (isPassed) passed++;
      else failed++;

      if (!r.isAbsent) {
        totalScoreSum += score;
        totalMaxSum += max;
      }

      examList.push({
        title: exam.title || 'آزمون',
        score,
        maxScore: max,
        percent,
        passed: isPassed,
        submittedAt: r.submittedAt,
      });
    });

    examList.sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));

    const avgPercent = totalMaxSum > 0 ? Math.round((totalScoreSum / totalMaxSum) * 100) : 0;

    // ═══════════════════════════════════════════════════════
    // ۵) نمودار آزمون‌ها (بار)
    // ═══════════════════════════════════════════════════════
    // همه به درصد ۰-۱۰۰ نرمال می‌شوند تا maxScoreهای متفاوت با هم مقایسه‌پذیر باشند
    const testsSeries = examList.slice(0, 8).map((e) => ({
      label: (e.title || 'آزمون').slice(0, 12),
      value: e.percent,           // ۰-۱۰۰
      rawScore: e.score,
      maxScore: e.maxScore,
      passed: e.passed,
    }));
    const testsYMax = 100;

    // ═══════════════════════════════════════════════════════
    // ۵.۵) سری تعداد آزمون‌های داده‌شده در هر بازه
    // ═══════════════════════════════════════════════════════
    const examsTakenSeries = buckets.map((b) => ({
      label: b.label,
      value: examList.filter((e) => {
        const d = new Date(e.submittedAt);
        return d >= b.start && d < b.end;
      }).length,
    }));
    const examsTakenCount = examsTakenSeries.reduce((s, d) => s + toNum(d.value), 0);

    // ═══════════════════════════════════════════════════════
    // ۶) ویدیوهای آموزشی (از lesson-progress + lesson-records)
    // ═══════════════════════════════════════════════════════
    const bySubjectVideos = {};
    const records = engine.get(`lesson-records-${userId}`, {}) || {};
    try {
      engine.keys(`lesson-progress-${userId}-`).forEach((key) => {
        const prog = engine.get(key, {}) || {};
        const completedIds = prog.completedStepIds || [];
        if (completedIds.length === 0) return;

        const visitedAt = toNum(prog.lastVisitedAt);
        if (visitedAt <= 0) return;               // ← بدون timestamp، skip
        const d = new Date(visitedAt);
        if (isNaN(d) || d < range.start || d >= range.end) return;
        

        const record = Object.values(records).find(
          (r) => r && r.grade && r.subject && r.folderName && r.lessonId &&
                 key === `lesson-progress-${userId}-${r.grade}-${r.subject}-${r.folderName}-${r.lessonId}`
        );
        if (!record) return;

        const lessonStepIds = new Set(record.lessonStepIds || []);
        const watchedVideos = completedIds.filter((id) => lessonStepIds.has(id)).length;
        if (watchedVideos === 0) return;

        const subj = record.subject || 'نامشخص';
        bySubjectVideos[subj] = (bySubjectVideos[subj] || 0) + watchedVideos;
      });
    } catch {}

    const videoSeries = Object.entries(bySubjectVideos)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        label: name,
        value,
        color: colorForSubject(name),
      }));

    // ═══════════════════════════════════════════════════════
    // ۷) کلاس‌های خصوصی (از appData.courses + enrolledCourses)
    // ═══════════════════════════════════════════════════════
    // ⚠️ type mismatch در includes → همه‌ی کلاس‌ها drop می‌شدند
    const enrolledIdSet = new Set(
      (Array.isArray(sourceUser.enrolledCourses) ? sourceUser.enrolledCourses : []).map(String)
    );
    const bySubjectClasses = {};

    (allCourses || []).forEach((c) => {
      if (!c || !enrolledIdSet.has(String(c.id))) return;
      if (c.status !== 'ended') return;

      const dateRaw = c.date ? new Date(c.date) : null;
      if (!dateRaw || isNaN(dateRaw)) return;
      if (dateRaw < range.start || dateRaw >= range.end) return;

      const subj = c.subject || (c.title || '').split(' ')[0] || 'عمومی';
      bySubjectClasses[subj] = (bySubjectClasses[subj] || 0) + 1;
    });

    const classSeries = Object.entries(bySubjectClasses)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        label: name,
        value,
        color: colorForSubject(name),
      }));

    // ═══════════════════════════════════════════════════════
    // ۸) چالش‌های حل‌شده (از exams.filter(isChallenge))
    // ═══════════════════════════════════════════════════════
    const challengeEvents = [];
    (exams || []).forEach((e) => {
      if (!e || !e.isChallenge) return;
      const r = e.results?.find((r) => String(r.studentId) === String(userId));
      if (!r || r.isFinalSubmit !== true || !r.submittedAt) return;
      const d = new Date(r.submittedAt);
      if (!isNaN(d)) challengeEvents.push(d);
    });

    const challengesSeries = buckets.map((b) => ({
      label: b.label,
      value: challengeEvents.filter((d) => d >= b.start && d < b.end).length,
    }));
    const challengeCount = challengesSeries.reduce((s, d) => s + toNum(d.value), 0);

    // ═══════════════════════════════════════════════════════
    // ۹) پیشرفت کلی (ترکیب منابع واقعی)
    // ═══════════════════════════════════════════════════════
    const overallProgress = computeOverallProgress(userId, sourceUser, exams);



    return {
      overallProgress,
      studySeries,
      overallSeries,
      xpSeries,
      xpFromExams,
      examsTakenSeries,
      examsTakenCount,
      testsSeries,
      testsYMax,
      examStats: {
        total: examList.length,
        passed,
        failed,
        pending,
        absent,
        avgPercent,
      },
      videoSeries,
      classSeries,
      challengesSeries,
      challengeCount,
      totalStudyMinutes,
      range,
    };
  }, [sourceUser, userId, exams, allCourses, selectedPeriod, dataTick]);



  // ─── Period indicator sliding
  const selectorRef = useRef(null);
  const indicatorRef = useRef(null);
  const btnRefs = useRef({});

  useEffect(() => {
    const place = () => {
      const sel = selectorRef.current, ind = indicatorRef.current;
      const btn = btnRefs.current[selectedPeriod];
      if (!sel || !ind || !btn) return;
      const selRect = sel.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      if (!btnRect.width) return;
      ind.style.width = `${btnRect.width}px`;
      ind.style.right = `${selRect.right - btnRect.right}px`;
      ind.style.left = 'auto';
    };
    place();
    const t = setTimeout(place, 80);
    window.addEventListener('resize', place);
    return () => { clearTimeout(t); window.removeEventListener('resize', place); };
  }, [selectedPeriod]);

  const periodLabel = PERIOD_LABEL[selectedPeriod] || '';

  const hasData =
    computed.totalStudyMinutes > 0 ||
    computed.examStats.total > 0 ||
    computed.challengeCount > 0 ||
    computed.videoSeries.length > 0 ||
    computed.classSeries.length > 0;

  // ─── Child not found
  if (isViewingChild && !childInfo) {
    return (
      <div className={`parent-dashboard ${isDark ? 'dark-theme' : ''}`}>
        <div className="app">
          <div className="empty-state-cta" style={{ minHeight: 400, marginTop: 40 }}>
            <div className="empty-icon">⚠️</div>
            <div className="empty-title">اطلاعات فرزند در دسترس نیست</div>
            <div className="empty-desc">
              داده‌های دانش‌آموز موردنظر یافت نشد. لطفاً دوباره تلاش کنید یا به داشبورد خود بازگردید.
            </div>
            <button
              className="icon-btn"
              onClick={() => navigate(-1)}
              style={{ marginTop: 16, width: 'auto', padding: '0 20px' }}
            >
              ↩ بازگشت
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Initial skeleton
  if (loading && !hasData) {
    return (
      <div className={`parent-dashboard ${isDark ? 'dark-theme' : ''}`}>
        <div className="app">
          <header className="page-header">
            <div className="header-top">
              <div className="breadcrumb">
                <span>وضعیت پیشرفت</span>
                <span className="sep">/</span>
                <span className="current">داشبورد</span>
              </div>
              <div className="header-actions">
                <button className="icon-btn" onClick={() => setIsDark((v) => !v)}>
                  {isDark ? '☀️' : '🌙'}
                </button>
              </div>
            </div>
            <div className="header-main">
              <div>
                <h1 className="page-title">
                  داشبورد <span className="page-title-accent">والدین</span>
                </h1>
                <p className="page-sub">در حال بارگذاری داده‌های {studentName}…</p>
              </div>
            </div>
          </header>
          <div className="special-divider"><span className="divider-core" /></div>
          <div className="charts-grid">
            <SkeletonCard full />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  // ─── Main render
  return (
    <div className={`parent-dashboard ${isDark ? 'dark-theme' : ''}`}>
      <div className="app">

        {/* ============== HEADER ============== */}
        <header className="page-header">
          <div className="header-top">
            <div className="breadcrumb">
              <span>پنل والدین</span>
              <span className="sep">/</span>
              <span className="current">داشبورد</span>
              {lastUpdate && (
                <>
                  <span className="sep">·</span>
                  <span className="last-update">به‌روزرسانی: {formatTime(lastUpdate)}</span>
                </>
              )}
              {loading && hasData && <span className="refreshing" title="در حال به‌روزرسانی">↻</span>}
            </div>
            <div className="header-actions">
              {/*<button
                className="icon-btn"
                onClick={() => setIsDark((v) => !v)}
                title="تغییر تم"
                aria-label="تغییر تم"
              >
                {isDark ? '☀️' : '🌙'}
              </button>*/}
              <button
                className="icon-btn"
                onClick={() => navigate(-1)}
                title="بازگشت"
                aria-label="بازگشت"
              >
                ↩
              </button>
            </div>
          </div>

          <div className="header-main">
            <div>
              <h1 className="page-title">
                داشبورد <span className="page-title-accent">والدین</span>
              </h1>
              <p className="page-sub">
                نگاهی دقیق به پیشرفت تحصیلی <strong>{studentName}</strong> · {periodLabel}
              </p>
            </div>
            <div className="student-chip">
              <div className="student-avatar">{studentName.charAt(0)}</div>
              <div className="student-info">
                <div className="student-name">{studentName}</div>
                <div className="student-meta">
                  {isViewingChild
                    ? `فرزند ${childInfo?.grade || ''}`
                    : (user?.role === 'parent' ? 'والد' : 'دانش‌آموز')}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* ============== PERIOD SELECTOR ============== */}
        <div className="period-wrap">
          <div className="period-selector" ref={selectorRef}>
            <span className="period-indicator" ref={indicatorRef} />
            {[/*'today',*/ 'week', 'month', 'year'].map((p) => (
              <button
                key={p}
                ref={(el) => { btnRefs.current[p] = el; }}
                className={selectedPeriod === p ? 'active' : ''}
                onClick={() => setSelectedPeriod(p)}
              >
                {{ today: 'روزانه', week: 'هفتگی', month: 'ماهانه', year: 'سالانه' }[p]}
              </button>
            ))}
          </div>
        </div>

        {/* ============== DIVIDER ============== */}
        <div className="special-divider">
          <span className="divider-core" />
        </div>

        {/* ============== KPI ROW ============== */}
        <div className="kpi-row">
          <div className="kpi-item">
            <div className="kpi-icon" style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="kpi-info">
              <span className="kpi-label">زمان مطالعه · {periodLabel}</span>
              <span className="kpi-value num">{formatDuration(computed.totalStudyMinutes)}</span>
            </div>
          </div>

          <div className="kpi-item">
            <div className="kpi-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="kpi-info">
              <span className="kpi-label">آزمون‌های قبول</span>
              <span className="kpi-value num">{fa(computed.examStats.passed)}</span>
            </div>
          </div>

          <div className="kpi-item">
            <div className="kpi-icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8B5CF6' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            </div>
            <div className="kpi-info">
              <span className="kpi-label">XP کل</span>
              <span className="kpi-value num">{totalXp.toLocaleString('fa-IR')}</span>
            </div>
          </div>

          <div className="kpi-item">
            <div className="kpi-icon" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
              </svg>
            </div>
            <div className="kpi-info">
              <span className="kpi-label">استریک</span>
              <span className="kpi-value num">{fa(streak)} <small>روز</small></span>
            </div>
          </div>
        </div>

        {/* ============== CHARTS GRID ============== */}
        <div className={`charts-grid ${loading && hasData ? 'is-refreshing' : ''}`}>

          {/* 1) OVERALL */}
          <div className="chart-card full">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="overall" color="#6366F1" bg="rgba(99,102,241,0.12)" />
                <div>
                  <div className="chart-title">پیشرفت تحصیلی</div>
                  <div className="chart-sub">
                    مطالعه روزانه · {periodLabel}
                  </div>
                </div>
              </div>
              <div className="chart-value-group">
                <div className="chart-value big num">{fa(computed.overallProgress)}٪</div>
                <div
                  className="chart-delta up"
                  title="۴۰٪ پیشرفت درس‌ها + ۴۰٪ میانگین آزمون‌ها + ۲۰٪ مطالعه‌ی ۷ روز اخیر"
                >
                  پیشرفت تحصیلی کلی
                </div>
              </div>
            </div>
            <div className="chart-body">
              <LineChartSVG
                series={computed.overallSeries}
                color="#6366F1"
                yMax={niceMax(Math.max(...computed.overallSeries.map((s) => toNum(s.value)), 1))}
                yMin={0}
                valueFormatter={(v) => `${fa(Math.round(v))} دقیقه`}
                yAxisFormatter={(v) => `${fa(Math.round(v))}د`}
                emptyText="هنوز زمان مطالعه‌ای ثبت نشده است"
              />
            </div>
          </div>

          {/* 2) STUDY TIME */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="study" color="#3B82F6" bg="rgba(59,130,246,0.12)" />
                <div>
                  <div className="chart-title">زمان مطالعه</div>
                  <div className="chart-sub">مجموع {periodLabel}</div>
                </div>
              </div>
              <div className="chart-value-group">
                <div className="chart-value num">{formatDuration(computed.totalStudyMinutes)}</div>
              </div>
            </div>
            <div className="chart-body">
              <BarChartSVG
                series={computed.studySeries}
                color="#3B82F6"
                yMax={niceMax(Math.max(...computed.studySeries.map((s) => toNum(s.value)), 1))}
                valueFormatter={(v) => `${fa(Math.round(v))} دقیقه`}
                yAxisFormatter={(v) => `${fa(Math.round(v))}د`}
                emptyText="هنوز زمانی برای مطالعه ثبت نشده"
              />
            </div>
          </div>

          {/* 3) USER XP 
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="points" color="#8B5CF6" bg="rgba(139,92,246,0.12)" />
                <div>
                  <div className="chart-title">امتیاز کاربر</div>
                  <div className="chart-sub">
                    مجموع XP · توزیع {periodLabel}
                  </div>
                </div>
              </div>
              <div className="chart-value-group">
                <div className="chart-value num">
                  {totalXp.toLocaleString('fa-IR')} <small>XP</small>
                </div>
              </div>
            </div>
            <div className="chart-body">
              <LineChartSVG
                series={computed.xpSeries}
                color="#8B5CF6"
                yMax={niceMax(Math.max(...computed.xpSeries.map((s) => toNum(s.value)), 1))}
                valueFormatter={(v) => `${fa(Math.round(v))} XP`}
                emptyText="هنوز XP در این بازه کسب نشده"
              />
            </div>
          </div>*/}

          {/* 3.5) EXAMS TAKEN TREND — نمودار خطی تعداد آزمون‌ها */}
          <div className="chart-card full">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="examsTaken" color="#F97316" bg="rgba(249,115,22,0.12)" />
                <div>
                  <div className="chart-title">روند تعداد آزمون‌ها</div>
                  <div className="chart-sub">تعداد آزمون‌های داده‌شده · {periodLabel}</div>
                </div>
              </div>
              <div className="chart-value-group">
                <div className="chart-value num">
                  {fa(computed.examsTakenCount)} <small>آزمون</small>
                </div>
              </div>
            </div>
            <div className="chart-body">
              {computed.examsTakenCount > 0 ? (
                <LineChartSVG
                  series={computed.examsTakenSeries}
                  color="#F97316"
                  yMax={niceMax(Math.max(...computed.examsTakenSeries.map((s) => toNum(s.value)), 1))}
                  valueFormatter={(v) => `${fa(Math.round(v))} آزمون`}
                  yAxisFormatter={(v) => fa(Math.round(v))}
                  emptyText="هنوز آزمونی در این بازه ثبت نشده"
                />
              ) : (
                <div className="chart-empty">هنوز آزمونی در این بازه ثبت نشده است</div>
              )}
            </div>
          </div>

          {/* 4) TESTS */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="tests" color="#10B981" bg="rgba(16,185,129,0.12)" />
                <div>
                  <div className="chart-title">نمرات ۸ آزمون اخیر</div>
                  <div className="chart-sub">
                    {fa(computed.examStats.total)} آزمون · {fa(computed.examStats.passed)} قبول
                  </div>
                </div>
              </div>
              <div className="chart-value-group">
                <div className="chart-value num">
                  {fa(computed.examStats.total)} <small>آزمون</small>
                </div>
                <div className="chart-delta up">
                  میانگین {fa(computed.examStats.avgPercent)}٪
                </div>
              </div>
            </div>
            <div className="chart-body">
              <BarChartSVG
                series={computed.testsSeries}
                yMax={100}
                colorFn={(v, i) => (computed.testsSeries[i]?.passed ? '#10B981' : '#EF4444')}
                valueFormatter={(v) => `${fa(Math.round(v))}٪`}
                yAxisFormatter={(v) => `${fa(Math.round(v))}٪`}
                emptyText="هنوز آزمونی ثبت نشده است"
              />
            </div>
            <div className="tests-summary">
              <div className="summary-item pass">
                <span className="summary-icon">✓</span>
                <span className="summary-num num">{fa(computed.examStats.passed)}</span>
                <span className="summary-lbl">قبول</span>
              </div>
              <div className="summary-item fail">
                <span className="summary-icon">✗</span>
                <span className="summary-num num">{fa(computed.examStats.failed)}</span>
                <span className="summary-lbl">مردود</span>
              </div>
              {computed.examStats.pending > 0 && (
                <div className="summary-item warn">
                  <span className="summary-icon">⏳</span>
                  <span className="summary-num num">{fa(computed.examStats.pending)}</span>
                  <span className="summary-lbl">در انتظار</span>
                </div>
              )}
              {computed.examStats.absent > 0 && (
                <div className="summary-item absent">
                  <span className="summary-icon">🚫</span>
                  <span className="summary-num num">{fa(computed.examStats.absent)}</span>
                  <span className="summary-lbl">غایب</span>
                </div>
              )}
            </div>
          </div>

          {/* 5) VIDEOS */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="videos" color="#F59E0B" bg="rgba(245,158,11,0.12)" />
                <div>
                  <div className="chart-title">ویدیوهای آموزشی</div>
                  <div className="chart-sub">
                    {computed.videoSeries.length
                      ? `توزیع بین دروس · ${periodLabel}`
                      : 'ویدیوی دیده‌شده‌ای ثبت نشده'}
                  </div>
                </div>
              </div>
              {computed.videoSeries.length > 0 && (
                <div className="chart-value-group">
                  <div className="chart-value num">
                    {fa(computed.videoSeries.reduce((s, x) => s + x.value, 0))}{' '}
                    <small>ویدیو</small>
                  </div>
                </div>
              )}
            </div>
            <div className="chart-body">
              {computed.videoSeries.length > 0 ? (
                <DonutChartSVG
                  series={computed.videoSeries}
                  centerLabel="ویدیو"
                  valueFormatter={(v) => `${fa(v)} ویدیو`}
                />
              ) : (
                <EmptyState
                  icon="🎬"
                  title="ویدیوی آموزشی دیده نشده"
                  desc="با مشاهده‌ی درس‌های کتاب، اینجا نمایش داده می‌شود"
                />
              )}
            </div>
          </div>

          {/* 6) CLASSES */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="classes" color="#EC4899" bg="rgba(236,72,153,0.12)" />
                <div>
                  <div className="chart-title">کلاس‌های خصوصی</div>
                  <div className="chart-sub">
                    {computed.classSeries.length
                      ? `توزیع بین دروس · ${periodLabel}`
                      : 'کلاس برگزارشده‌ای ثبت نشده'}
                  </div>
                </div>
              </div>
              {computed.classSeries.length > 0 && (
                <div className="chart-value-group">
                  <div className="chart-value num">
                    {fa(computed.classSeries.reduce((s, x) => s + x.value, 0))}{' '}
                    <small>کلاس</small>
                  </div>
                </div>
              )}
            </div>
            <div className="chart-body">
              {computed.classSeries.length > 0 ? (
                <DonutChartSVG
                  series={computed.classSeries}
                  centerLabel="کلاس"
                  valueFormatter={(v) => `${fa(v)} کلاس`}
                />
              ) : (
                <EmptyState
                  icon="👥"
                  title="کلاس خصوصی برگزار نشده"
                  desc="پس از شرکت در کلاس‌ها، آمار اینجا نمایش داده می‌شود"
                />
              )}
            </div>
          </div>

          {/* 7) CHALLENGES */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title-group">
                <ChartIcon name="challenges" color="#06B6D4" bg="rgba(6,182,212,0.12)" />
                <div>
                  <div className="chart-title">چالش‌های حل‌شده</div>
                  <div className="chart-sub">روند در {periodLabel}</div>
                </div>
              </div>
              <div className="chart-value-group">
                <div className="chart-value num">
                  {fa(computed.challengeCount)} <small>چالش</small>
                </div>
              </div>
            </div>
            <div className="chart-body">
              <LineChartSVG
                series={computed.challengesSeries}
                color="#06B6D4"
                yMax={niceMax(Math.max(...computed.challengesSeries.map((s) => toNum(s.value)), 1))}
                valueFormatter={(v) => `${fa(v)} چالش`}
                emptyText="هنوز چالشی در این بازه حل نشده است"
              />
            </div>
          </div>

        </div>

        

      </div>
    </div>
  );
};

export default ParentDashboard;